/**
 * "Start from an approved document" — the entry screen's first way into the
 * Studio. Lists the knowledge library's approved documents (the same finder
 * the Studio's source pane uses) and opens the Studio on one as the new
 * process's source. No upload here: a document reaches this list through the
 * knowledge library's own intake and review.
 *
 * Four async states per frontend/CLAUDE.md. Search runs on submit, not per
 * keystroke: it is the hybrid content search, not a local filter.
 */
import { Button, Skeleton, formatDayStamp } from '@ai4nr/tricorder-ui'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { FileText, Search } from 'lucide-react'

import { useKnowledgeCategories } from '@/hooks/useKnowledgeCategories'
import { findSourceDocuments, sourceDocumentsKey, type SourceDocument } from '../../lib/studio/sourceDocuments'

export function ApprovedDocuments({ onStart }: { onStart: (documentId: string) => void }) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState('')
  const categories = useKnowledgeCategories()
  const categoryLabels = useMemo(() => new Map(categories.map((c) => [c.value, c.label])), [categories])

  const list = useQuery({
    queryKey: sourceDocumentsKey(submitted),
    queryFn: () => findSourceDocuments(submitted),
  })

  return (
    <section
      aria-labelledby="alg-from-document"
      className="flex min-w-0 flex-col gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] p-5 sm:p-6"
      data-testid="alg-from-document"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-64">
          <h2 id="alg-from-document" className="text-[19px] font-semibold text-[var(--color-surface-ink)]">
            {t('alg.manage.fromDocument.title', 'Start from an approved document')}
          </h2>
          <p className="text-[14px] text-[var(--color-surface-mute)]">
            {t('alg.manage.fromDocument.hint', 'Documents already approved in your knowledge library. Pick one to model its steps in the Studio.')}
          </p>
        </div>
        <form
          role="search"
          onSubmit={(e) => {
            e.preventDefault()
            setSubmitted(query)
          }}
          className="relative w-full sm:w-64"
        >
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-surface-mute)]"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              // Clearing the field returns to the library list without a submit.
              if (e.target.value === '') setSubmitted('')
            }}
            placeholder={t('alg.manage.fromDocument.search', 'Search approved documents')}
            aria-label={t('alg.manage.fromDocument.search', 'Search approved documents')}
            className="h-11 w-full rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-canvas,#ffffff)] pl-9 pr-4 text-[15px] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-500)]"
            data-testid="alg-from-document-search"
          />
        </form>
      </div>

      {list.isError && !list.data ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 border-t border-[var(--color-surface-border)] pt-3">
          <p className="text-[14px] text-[var(--color-tone-bad)]">
            {t('alg.manage.fromDocument.failed', 'Could not load documents.')}
          </p>
          <Button size="sm" variant="outline" onClick={() => void list.refetch()}>
            {t('alg.run.retry', 'Try again')}
          </Button>
        </div>
      ) : list.isLoading ? (
        <div className="space-y-2 border-t border-[var(--color-surface-border)] pt-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12 w-full rounded-[10px]" />
          ))}
        </div>
      ) : (list.data ?? []).length === 0 ? (
        <p className="border-t border-[var(--color-surface-border)] pt-3 text-[14px] text-[var(--color-surface-mute)]">
          {submitted.trim()
            ? t('alg.manage.fromDocument.noMatch', 'No approved documents match.')
            : t('alg.manage.fromDocument.empty', 'No approved documents yet.')}
        </p>
      ) : (
        <ul
          className="max-h-80 overflow-y-auto"
          aria-busy={list.isFetching}
          data-testid="alg-from-document-list"
        >
          {(list.data ?? []).map((d) => (
            <DocumentRow
              key={d.id}
              document={d}
              categoryLabel={categoryLabels.get(d.category) ?? d.category}
              onStart={() => onStart(d.id)}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

function DocumentRow({
  document,
  categoryLabel,
  onStart,
}: {
  document: SourceDocument
  categoryLabel: string
  onStart: () => void
}) {
  const { t } = useTranslation()
  const meta = [
    document.updatedAt
      ? t('alg.manage.fromDocument.updated', 'Updated {{date}}', { date: formatDayStamp(document.updatedAt, t) })
      : null,
    categoryLabel || null,
  ].filter((part): part is string => part !== null)

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-[var(--color-surface-border)] py-3">
      <FileText aria-hidden="true" className="h-5 w-5 shrink-0 text-[var(--color-primary-500)]" />
      <div className="min-w-0 flex-1 basis-40">
        <p className="truncate text-[15px] font-semibold text-[var(--color-surface-ink)]">{document.title}</p>
        {meta.length > 0 && (
          <p className="truncate text-[13px] text-[var(--color-surface-mute)]">{meta.join(' · ')}</p>
        )}
      </div>
      <Button size="pill" onClick={onStart} className="shrink-0" data-testid={`alg-from-document-${document.id}`}>
        {t('alg.manage.fromDocument.make', 'Make a process')}
      </Button>
    </li>
  )
}
